import { useTranslation } from "react-i18next";
import { IconShieldCheck, IconShieldLock } from "@tabler/icons-react";
import { cn } from "../../lib/utils";
import type { ContactLineKind } from "../../tools/analytics/domains/partners.analytics";

/**
 * The line at the bottom of the welcome sheet and of the room info sheet that
 * says what the partner receives (docs/partner-objection.md, REQ-07). It never
 * links to the settings: the objection is managed in "Partner sharing".
 *
 * `dialog` is the desktop dialog's line (Figma Desktop 5588:229733): a 14px
 * shield-check and 13/18 text, 4px under the block above it.
 */
export function RoomContactLine({
  kind,
  partnerName,
  presentation,
}: {
  kind: Exclude<ContactLineKind, "none">;
  partnerName: string;
  presentation: "sheet" | "dialog";
}): React.JSX.Element {
  const { t } = useTranslation();
  const text = (() => {
    switch (kind) {
      case "informs":
        return t(
          "{{partner}} receives your email address and may contact you for professional communications. You can object anytime in Settings.",
          { partner: partnerName }
        );
      case "conditional":
        return t(
          "If you join this room, {{partner}} will receive your email address and may contact you for professional communications. You can object anytime in Settings.",
          { partner: partnerName }
        );
      case "confirms":
        return t(
          "Your objection applies: {{partner}} doesn't receive your email address. Manage it in Settings.",
          { partner: partnerName }
        );
      default:
        throw new Error(kind satisfies never);
    }
  })();

  return (
    <div
      className={cn(
        "flex w-full items-start gap-2",
        presentation === "dialog" && "pt-1"
      )}
    >
      {presentation === "dialog" ? (
        <IconShieldCheck
          className="size-3.5 shrink-0 text-ds-slate-700"
          stroke={1.5}
        />
      ) : (
        <IconShieldLock
          className="mt-px size-4 shrink-0 text-ds-slate-600"
          stroke={1.5}
        />
      )}
      <p
        className={cn(
          "m-0 flex-1 font-dm-sans text-ds-slate-600",
          presentation === "dialog" ? "text-desktop-meta" : "text-caption"
        )}
      >
        {text}
      </p>
    </div>
  );
}
